import { DownloadOutlined } from "@ant-design/icons";
import { skipToken } from "@reduxjs/toolkit/query";
import { Divider, Modal } from "antd";
import { useExportTableDataMutation } from "components";
import i18n from "i18next";
import { type FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { orderTableNamesForExport } from "src/app/features/ConfigurationTransfer/configurationTransferUtils";
import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { Checkbox } from "../../../../../components/Form/formComponents";
import { NotificationType } from "../../../../../components/NotificationAlerts";
import { NotificationAlert } from "../../../../../components/NotificationAlerts/NotificationAlert";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { showInfoNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { useGetTransferableEntitiesQuery } from "../../services/server/ConfigurationTransferApi";
import { TransferableEntitiesTree } from "../trees/TransferableEntitiesTree";

export interface ExportTablesSelectionModalProps {
    buttonTextKey?: string;
}

export const ExportTablesSelectionModal: FC<ExportTablesSelectionModalProps> = ({ buttonTextKey = "button.exportConfig" }) => {
    const dataGroup = getAssignedDataGroup();
    const dispatch = useDispatch();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [exportAll, setExportAll] = useState(true);
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

    const queryArg = isModalVisible && dataGroup?.id ? Number(dataGroup.id) : skipToken;

    const { data: entitiesData, isLoading, error } = useGetTransferableEntitiesQuery(queryArg);

    const [exportDataRequest, { error: exportDataError }] = useExportTableDataMutation();

    const notifyInfo = useCallback(() => {
        dispatch(
            showInfoNotification({
                text: i18n.t("info.export"),
                autoCloseDisabled: false,
            }),
        );
    }, [dispatch]);

    const showModal = () => {
        setIsModalVisible(true);
        setExportAll(true);
        setCheckedKeys([]);
    };

    const handleClose = () => {
        setIsModalVisible(false);
        setExportAll(true);
        setCheckedKeys([]);
    };

    const handleExportAllChange = (checked: boolean) => {
        setExportAll(checked);

        if (checked) {
            setCheckedKeys([]);
        }
    };

    const handleTreeCheck = (checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
        const keys = Array.isArray(checked) ? checked : checked.checked;

        const dependencyMap = new Map((entitiesData || []).map((entity) => [entity.tableName, entity.dependencies || []]));
        const allowedTables = new Set((entitiesData || []).filter((entity) => entity.allowed).map((entity) => entity.tableName));

        const normalizeName = (name: string) => name.trim();
        const isValidTableName = (name: string) => name.length > 0 && name !== "N/A" && allowedTables.has(name);

        const validKeys = keys
            .map((key) => key.toString())
            .map(normalizeName)
            .filter(isValidTableName);

        const expandedTables = new Set<string>();
        const visiting = new Set<string>();

        const collectDependencies = (tableName: string) => {
            if (!isValidTableName(tableName) || expandedTables.has(tableName) || visiting.has(tableName)) {
                return;
            }

            visiting.add(tableName);
            expandedTables.add(tableName);

            const dependencies = dependencyMap.get(tableName) || [];
            dependencies
                .map(normalizeName)
                .filter(isValidTableName)
                .forEach((dependency) => collectDependencies(dependency));

            visiting.delete(tableName);
        };

        validKeys.forEach((tableName) => collectDependencies(tableName));

        setCheckedKeys(Array.from(expandedTables));
    };

    const handleExport = async () => {
        const baseUrl = `${ResourceName.DATA_GROUPS}/${dataGroup.id}/config-export`;
        let url = baseUrl;

        if (exportAll) {
            // When "Export All" is checked, export all transferable entities in the predefined order
            if (entitiesData && entitiesData.length > 0) {
                const orderedTableNames = orderTableNamesForExport(entitiesData.map((entity) => entity.tableName));
                const tableNames = orderedTableNames.join(",");
                url = `${baseUrl}?tables=${tableNames}`;
            }
        } else {
            // When specific tables are selected
            if (checkedKeys.length === 0) {
                return;
            }

            const selectedTableNames = checkedKeys.map((key) => key.toString());
            const orderedTableNames = orderTableNamesForExport(selectedTableNames);
            const tableNames = orderedTableNames.join(",");

            url = `${baseUrl}?tables=${tableNames}`;
        }

        exportDataRequest({
            url,
            method: "GET",
            downloadFileName: ResourceName.CONFIGURATION_TRANSFER,
        });

        if (!exportDataError) {
            notifyInfo();
        }
        handleClose();
    };

    const isExportDisabled = !exportAll && checkedKeys.length === 0;

    return (
        <>
            <SecondaryButton onClick={showModal} icon={<DownloadOutlined />} name="configuration_transfer_export_button">
                {i18n.t(buttonTextKey)}
            </SecondaryButton>

            <Modal
                title={i18n.t("configurationTransfer.modal.title")}
                open={isModalVisible}
                onCancel={handleClose}
                width={800}
                footer={[
                    <SecondaryButton key="close" name="configuration_transfer_export_close" onClick={handleClose}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton
                        key="export"
                        name="configuration_transfer_export_confirm"
                        onClick={handleExport}
                        disabled={isExportDisabled}
                    >
                        {i18n.t("button.export")}
                    </PrimaryButton>,
                ]}
            >
                {exportAll && (
                    <NotificationAlert
                        titleKey={i18n.t("configurationTransfer.modal.exportAllInfo")}
                        type={NotificationType.INFO}
                        description={""}
                    />
                )}
                {!exportAll && (
                    <NotificationAlert
                        type={NotificationType.INFO}
                        titleKey={i18n.t("configurationTransfer.tree.dependencyInfo.description")}
                        description={""}
                    />
                )}

                {/* Export All Checkbox */}
                <Checkbox
                    name="export_all_tables_Checkbox"
                    value={exportAll}
                    onChange={handleExportAllChange}
                    label="configurationTransfer.modal.exportAll"
                    readOnly={false}
                />

                <Divider />

                {/* Transferable Entities Tree */}
                {error ? (
                    <NotificationAlert
                        titleKey={i18n.t("core.notifications.error.title")}
                        type={NotificationType.ERROR}
                        description={i18n.t("configurationTransfer.error.loadEntities")}
                    />
                ) : (
                    <TransferableEntitiesTree
                        entities={entitiesData || []}
                        checkedKeys={checkedKeys}
                        onCheck={handleTreeCheck}
                        disabled={exportAll}
                        loading={isLoading}
                    />
                )}

                {/* Validation Warning */}
                {!exportAll && checkedKeys.length === 0 && (
                    <NotificationAlert
                        titleKey={i18n.t("configurationTransfer.validation.noTablesSelected")}
                        type={NotificationType.WARNING}
                        description={""}
                    />
                )}
                {/* Selection Count */}
                {!exportAll && checkedKeys.length > 0 && (
                    <NotificationAlert
                        titleKey={i18n.t("configurationTransfer.modal.selectedCount", { count: checkedKeys.length })}
                        type={NotificationType.WARNING}
                        description={""}
                    />
                )}
            </Modal>
        </>
    );
};
